import { doc, Firestore, writeBatch } from "@angular/fire/firestore";
import { IGroupMap } from "../interfaces";
import { DocumentReference, updateDoc } from "firebase/firestore";

export const updateGroupUsers = async (
  orgId: string,
  updatedGroups: IGroupMap,
  firestore: Firestore
) => {
  const batch = writeBatch(firestore);
  // Update the org with the updated groups
  Object.keys(updatedGroups).forEach((groupId) => {
    const groupDoc = doc(firestore, "organization", orgId, "groups", groupId);
    batch.update(groupDoc, {
      userIds: updatedGroups[groupId].userIds,
    });
  });

  // Commit the batch
  await batch.commit();
};

/**
 * Updates a Firestore document by flattening deeply nested objects into dot-notated keys.
 *
 * Firestore's `update()` method requires all nested fields to be flattened using dot notation,
 * such as `ownerRelationships.user123.capTableDetails.A` instead of passing a deeply nested object.
 * This utility function automates that process to avoid repetitive boilerplate code.
 *
 * @param docRef - A reference to the Firestore document to update.
 * @param data - An object containing potentially nested fields that will be flattened.
 *
 * @example
 * const newRelationship = {
 *   id: '1',
 *   capTableDetails: { '1': 5 },
 *   ownerType: ['Entity']
 * };
 *
 * await updateDocWithFlatten(docRef, {
 *   ['ownerRelationships.1']: newRelationship
 * });
 */
export async function updateDocWithFlatten(
  docRef: DocumentReference,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: Record<string, any>
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const flattened: Record<string, any> = {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const flatten = (obj: Record<string, any>, prefix = '') => {
    for (const [key, value] of Object.entries(obj)) {
      const newKey = prefix ? `${prefix}.${key}` : key;

      if (
        value !== null &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        !(value instanceof Date)
      ) {
        flatten(value, newKey);
      } else {
        flattened[newKey] = value;
      }
    }
  };

  flatten(data);

  await updateDoc(docRef, flattened);
}
